// src/contexts/ChatProvider.tsx
import React, { createContext, useContext, useState } from "react";
import { SettingsData, Conversation, ToolFunction } from "./settings/types";
import { defaultSettings } from "./settings/chat";

interface ChatContextType {
  settings: SettingsData;
  updateSettings: (settings: Partial<SettingsData>) => void;
  conversation: Conversation | null;
  startNewConversation: () => void;
  addTool: (tool: ToolFunction) => void;
  removeTool: (toolName: string) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function ChatProvider({ children }: { children: React.ReactNode }) {
  // Initialize settings with predefined tools
  const initialSettings = {
    ...defaultSettings,
    tools: {
      ...defaultSettings.tools,
    },
  };

  const [settings, setSettings] = useState<SettingsData>(initialSettings);
  const [conversation, setConversation] = useState<Conversation | null>(null);

  const updateSettings = (newSettings: Partial<SettingsData>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  };

  const startNewConversation = () => {
    setConversation({
      id: crypto.randomUUID(),
      agentId: "default",
      title: "New Conversation",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      messages: [],
    });
  };

  const addTool = (tool: ToolFunction) => {
    // Check if tool already exists
    if (settings.tools.functions.some((t) => t.name === tool.name)) {
      return; // Tool already exists
    }
    setSettings((prev) => ({
      ...prev,
      tools: {
        ...prev.tools,
        functions: [...prev.tools.functions, tool],
      },
    }));
  };

  const removeTool = (toolName: string) => {
    setSettings((prev) => ({
      ...prev,
      tools: {
        ...prev.tools,
        functions: prev.tools.functions.filter(
          (tool) => tool.name !== toolName
        ),
      },
    }));
  };

  return (
    <ChatContext.Provider
      value={{
        settings,
        updateSettings,
        conversation,
        startNewConversation,
        addTool,
        removeTool,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export function useChat() {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
